import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Sparkles,
  Palette,
  Compass,
  Lightbulb,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Layers,
  Award,
  ShieldCheck,
  Send,
  Building2,
  FileCheck,
  Image as ImageIcon,
  PenTool,
  Grid,
  Maximize2,
  Download,
  Eye,
  TrendingUp,
} from "lucide-react";

export const Route = createFileRoute("/services_/logo-design")({
  head: () => ({
    meta: [
      {
        title:
          "Logo Design Services — KMWAdvertising LLP | Logos That Become Business Assets",
      },
      {
        name: "description",
        content:
          "Professional Logo Design Services by KMWAdvertising LLP. We create memorable, scalable, and timeless logos designed to build strong brand identities and business assets.",
      },
      {
        property: "og:title",
        content: "Logo Design Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Transform your brand identity with expert logo design. Discovery, concept development, refinement, and comprehensive brand identity deliverables.",
      },
    ],
  }),
  component: LogoDesignServicePage,
});

export function LogoDesignServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const processSteps = [
    {
      title: "Discovery",
      desc: "Understanding your business, audience and competitors.",
      icon: Compass,
      badge: "Stage 01",
    },
    {
      title: "Concept Development",
      desc: "Exploring multiple creative directions.",
      icon: Lightbulb,
      badge: "Stage 02",
    },
    {
      title: "Refinement",
      desc: "Selecting and refining the strongest concepts.",
      icon: PenTool,
      badge: "Stage 03",
    },
    {
      title: "Final Identity",
      desc: "Delivering logos suitable for digital, print and large-format applications.",
      icon: CheckCircle2,
      badge: "Stage 04",
    },
  ];

  const deliverables = [
    "Primary Logo",
    "Secondary Logo",
    "Icon Version",
    "Monochrome Versions",
    "Colour Variations",
    "Social Media Versions",
    "Print Files",
    "Vector Artwork",
    "Brand Usage Guidelines",
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-rose-600/15 to-purple-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">Logo Design</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Logo Design Services
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Logo Design
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                Creating Logos That Become Business Assets
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <p>
                A logo is often the first visual element customers associate with your business. An effective logo should be memorable, scalable and timeless.
              </p>
              <p className="text-sm sm:text-base text-slate-400">
                Rather than following short-term design trends, we create logos designed to remain relevant for years.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#logo-brief-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Start Logo Design Project</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#logo-process"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore Our Process</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Design Process */}
      <section id="logo-process" className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <h2 className="text-xs font-mono font-bold uppercase tracking-widest text-pink-400">
              Structured & Creative Workflow
            </h2>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white">
              Our Logo Design Process Includes:
            </h3>
            <p className="text-slate-300 text-sm sm:text-base">
              Every iconic brand identity is built on a methodical creative methodology that balances strategy with artistic excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step) => {
              const StepIcon = step.icon;
              return (
                <div
                  key={step.title}
                  className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition-all duration-300 relative group"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-pink-400 bg-pink-500/10 px-2.5 py-1 rounded-full border border-pink-500/20">
                      {step.badge}
                    </span>
                    <div className="h-10 w-10 rounded-2xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center text-pink-400 group-hover:scale-110 transition-transform">
                      <StepIcon className="h-5 w-5" />
                    </div>
                  </div>

                  <h4 className="text-xl font-bold text-white mb-2">{step.title}</h4>
                  <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
                <FileCheck className="h-3.5 w-3.5" />
                Comprehensive Brand Package
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Deliverables Include:
              </h2>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                When you partner with KMWAdvertising LLP, you receive a full multi-format identity kit ready for instant deployment across web, broadcast TV, print media, merchandise, and large-format outdoor displays.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {deliverables.map((item) => (
                  <div
                    key={item}
                    className="p-3.5 rounded-2xl bg-white/[0.02] border border-white/10 flex items-center gap-3 hover:border-pink-500/30 transition"
                  >
                    <div className="h-6 w-6 rounded-lg bg-pink-500/10 flex items-center justify-center shrink-0">
                      <Check className="h-3.5 w-3.5 text-pink-400" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="p-8 sm:p-10 rounded-3xl bg-gradient-to-tr from-pink-950/30 via-[#0d0d1a] to-purple-950/30 border border-white/15 relative overflow-hidden space-y-6">
                <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />
                
                <h3 className="text-xl font-bold text-white relative z-10">
                  Built for Timeless Impact & Equity
                </h3>

                <blockquote className="text-slate-300 text-sm sm:text-base italic border-l-2 border-pink-400 pl-4 py-1 leading-relaxed relative z-10">
                  "Rather than following short-term design trends, we create logos designed to remain relevant for years."
                </blockquote>

                <div className="space-y-3 text-xs text-slate-400 leading-relaxed relative z-10 pt-2 border-t border-white/10">
                  <p>
                    A well-designed logo creates immediate recognition, conveys professional credibility, and anchors your marketing communications across every channel.
                  </p>
                  <p>
                    With 25+ years of advertising and brand building experience, our creative directors ensure your visual identity stands out cleanly in competitive markets.
                  </p>
                </div>

                <div className="pt-4 relative z-10">
                  <Link
                    to="/services/branding-creatives"
                    className="inline-flex items-center gap-2 text-xs font-mono font-bold text-pink-300 hover:text-white uppercase tracking-wider transition"
                  >
                    <span>View All Branding & Creative Services</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brief & Consultation Form */}
      <section id="logo-brief-form" className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Custom Identity Brief
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Ready to Build a Memorable Logo Asset?
              </h2>
              <p className="text-slate-300 text-sm">
                Share your brand vision with our senior creative directors. We will craft custom concepts tailored to your industry and growth goals.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Logo Design Brief Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our creative director will connect with you within 24 hours to schedule your identity discovery call.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Vikram Sharma"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Acme Tech Solutions"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="vikram@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Logo Design Requirement</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="new">New Brand Logo Design</option>
                    <option value="rebrand">Brand Rebrand / Logo Redesign</option>
                    <option value="guidelines">Complete Visual Identity & Guidelines</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Project Notes / Vision</label>
                  <textarea
                    rows={4}
                    placeholder="Describe your brand values, target audience, preferred colors, or existing ideas..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Submit Logo Design Brief</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
